import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/atoms/Button/Button';
import { Input } from '../components/atoms/Input/Input';
import { Text } from '../components/atoms/Typography/Text';
import { AuthLayout } from '../components/templates/AuthLayout';
import { useAuth } from '../context/AuthContext';
import type { MfaChallenge } from '../services/authService';
import { useTranslation } from '../i18n/useTranslation';
import { isAccountDeleted, subscribeAccountDeleted } from '../lib/accountDeleted';
import { isSessionExpired, subscribeSessionExpired } from '../lib/sessionExpired';
import { IDLE_SESSION_POLICY, isIdleSignedOut, policyMinutes, subscribeIdleSignedOut } from '../lib/idleSession';
import { safeRedirectPath } from '../lib/redirectPath';
import { ACCOUNT_PENDING_PATH, markAccountApproval } from '../lib/accountApproval';
import { getEmailErrorKey, getLoginPasswordErrorKey } from '../lib/authValidation';
import './LoginPage.css';
import { Alert } from '../components/atoms/Alert/Alert';
import { BetaAccessNotice } from '../components/auth/BetaAccessNotice/BetaAccessNotice';

interface ValidationErrors {
  email?: string;
  password?: string;
}

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login, confirmMfa } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [serverError, setServerError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // Second step for accounts with two-factor authentication (authenticator app).
  const [mfaChallenge, setMfaChallenge] = useState<MfaChallenge | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaError, setMfaError] = useState('');
  const mfaCodeRef = useRef<HTMLInputElement>(null);
  // Set when the API answered 401 ACCOUNT_DELETED (see lib/accountDeleted.ts).
  const accountDeleted = useSyncExternalStore(subscribeAccountDeleted, isAccountDeleted, isAccountDeleted);
  // Set when the session could not be renewed (see lib/sessionExpired.ts).
  const sessionExpired = useSyncExternalStore(subscribeSessionExpired, isSessionExpired, isSessionExpired);
  // Set when the user was signed out after the idle timeout (see lib/idleSession.ts).
  const idleSignedOut = useSyncExternalStore(subscribeIdleSignedOut, isIdleSignedOut, isIdleSignedOut);
  // After signing in, go back to the page the user was on (?redirect=, same-origin paths only).
  const [searchParams] = useSearchParams();
  const afterLoginPath = safeRedirectPath(searchParams.get('redirect')) ?? '/dashboard';

  function validate(): ValidationErrors {
    const errors: ValidationErrors = {};
    const emailError = getEmailErrorKey(email);
    if (emailError) errors.email = t(emailError);
    const passwordError = getLoginPasswordErrorKey(password);
    if (passwordError) errors.password = t(passwordError);
    return errors;
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setServerError('');
    const errors = validate();
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setIsLoading(true);
    try {
      const result = await login(email, password);
      if (result.type === 'success') {
        navigate(afterLoginPath);
      } else if (result.type === 'mfa_required') {
        setMfaCode('');
        setMfaError('');
        setMfaChallenge(result.challenge);
      } else if (result.type === 'approval_required') {
        // Closed beta: no session for an account the admin has not approved.
        markAccountApproval(result.state);
        navigate(ACCOUNT_PENDING_PATH);
      } else {
        setServerError(t('auth.loginFailed'));
      }
    } catch {
      setServerError(t('auth.loginFailed'));
    } finally {
      setIsLoading(false);
    }
  }

  // Focus the code field when the second step appears.
  useEffect(() => {
    if (mfaChallenge) mfaCodeRef.current?.focus();
  }, [mfaChallenge]);

  async function handleMfaSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (!mfaChallenge || mfaCode.length !== 6) return;
    setMfaError('');
    setIsLoading(true);
    try {
      const result = await confirmMfa(email, mfaChallenge, mfaCode);
      if (result.type === 'success') {
        navigate(afterLoginPath);
      } else if (result.type === 'approval_required') {
        markAccountApproval(result.state);
        navigate(ACCOUNT_PENDING_PATH);
      } else if (result.reason === 'expired') {
        // The sign-in session ran out (Cognito: 3 minutes) — start again.
        setMfaChallenge(null);
        setPassword('');
        setServerError(t('auth.mfa.challenge.errors.expired'));
      } else {
        setMfaCode('');
        setMfaError(t(result.reason === 'invalid_code' ? 'auth.mfa.challenge.errors.invalidCode' : 'auth.mfa.challenge.errors.generic'));
        mfaCodeRef.current?.focus();
      }
    } catch {
      setMfaError(t('auth.mfa.challenge.errors.generic'));
    } finally {
      setIsLoading(false);
    }
  }

  function backToPassword(): void {
    setMfaChallenge(null);
    setMfaCode('');
    setMfaError('');
    setPassword('');
  }

  if (mfaChallenge) {
    return (
      <AuthLayout title={t('auth.mfa.challenge.title')} subtitle={t('auth.mfa.challenge.subtitle')}>
        <form
          onSubmit={handleMfaSubmit}
          className="login-page__form"
          aria-label={t('auth.mfa.challenge.title')}
          noValidate
        >
          <div className="login-page__field">
            <Input
              ref={mfaCodeRef}
              id="mfa-code"
              name="mfa-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]*"
              maxLength={6}
              label={t('auth.mfa.challenge.codeLabel')}
              placeholder={t('auth.mfa.challenge.codePlaceholder')}
              value={mfaCode}
              onChange={(e) => {
                setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                setMfaError('');
              }}
              error={mfaError || undefined}
              required
              disabled={isLoading}
            />
          </div>

          <Button
            type="submit"
            variant="primary"
            isLoading={isLoading}
            disabled={isLoading || mfaCode.length !== 6}
            size="lg"
            fullWidth
            className="login-page__submit"
          >
            {isLoading ? t('auth.mfa.challenge.verifying') : t('auth.mfa.challenge.verifyButton')}
          </Button>
          <Button type="button" variant="ghost" onClick={backToPassword} disabled={isLoading}>
            {t('auth.mfa.challenge.back')}
          </Button>
        </form>
      </AuthLayout>
    );
  }

  function clearFieldError(field: keyof ValidationErrors) {
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  }

  return (
    <AuthLayout title={t('auth.signIn')} subtitle={t('auth.loginSubtitle')}>
      <form
        onSubmit={handleSubmit}
        className="login-page__form"
        aria-label={t('auth.loginFormLabel')}
        noValidate
      >
        <BetaAccessNotice compact />
        {accountDeleted && (
          <Alert tone="info">{t('auth.accountDeletedNotice')}</Alert>
        )}
        {idleSignedOut && !accountDeleted && (
          <Alert tone="info" data-testid="idle-signed-out-notice">
            {t('auth.idle.notice', { count: policyMinutes(IDLE_SESSION_POLICY.timeoutMs) })}
          </Alert>
        )}
        {sessionExpired && !accountDeleted && !idleSignedOut && (
          <Alert tone="info">{t('auth.sessionExpiredNotice')}</Alert>
        )}

        <div className="login-page__field">
          <Input
            id="email"
            name="email"
            type="email"
            label={t('auth.emailLabel')}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              clearFieldError('email');
            }}
            error={validationErrors.email}
            autoComplete="email"
            required
            disabled={isLoading}
          />
        </div>

        <div className="login-page__field">
          <Input
            id="password"
            name="password"
            type="password"
            label={t('auth.passwordLabel')}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              clearFieldError('password');
            }}
            error={validationErrors.password}
            autoComplete="current-password"
            required
            disabled={isLoading}
          />
        </div>

        <div className="login-page__forgot">
          <Link to="/reset-password" className="login-page__link">
            {t('auth.forgotPasswordLink')}
          </Link>
        </div>

        {serverError && (
          <Alert>{serverError}</Alert>
        )}

        <Button
          type="submit"
          variant="primary"
          isLoading={isLoading}
          disabled={isLoading}
          size="lg"
          fullWidth
          className="login-page__submit"
        >
          {isLoading ? t('auth.signingIn') : t('auth.signIn')}
        </Button>
      </form>

      <div className="login-page__footer">
        <Text className="login-page__register-prompt">
          {t('auth.noAccount')}{' '}
          <Link to="/register" className="login-page__link">
            {t('auth.registerLink')}
          </Link>
        </Text>
      </div>
    </AuthLayout>
  );
}

export default LoginPage;
